import { useState } from 'react';
import {
  SITE_BETA_TOOLBAR_COPY,
  SITE_BETA_TOOLBAR_MINIMIZED_KEY,
} from '../constants/siteDisclaimer';
import { useShare } from '../ShareContext';
import { HOME_PAGE_SHARE } from '../sharePayload';
import { trackFeedbackOpen } from '../ga';
import { FacebookFollowLink } from './FacebookFollowLink';
import { FeedbackModal } from './FeedbackModal';
import './BetaBottomToolbar.css';

function readBetaToolbarMinimized() {
  try {
    return localStorage.getItem(SITE_BETA_TOOLBAR_MINIMIZED_KEY) === '1';
  } catch {
    return false;
  }
}

function ShareIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"
      />
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points="16 6 12 2 8 6"
      />
      <line
        x1="12"
        y1="2"
        x2="12"
        y2="15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function FeedbackIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ChevronUpIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

/**
 * @param {{ visible: boolean }} props
 */
export function BetaBottomToolbar({ visible }) {
  const { openShareModal } = useShare();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [minimized, setMinimized] = useState(readBetaToolbarMinimized);

  if (!visible) {
    return null;
  }

  function handleShare() {
    openShareModal({ ...HOME_PAGE_SHARE, location: 'beta_toolbar' });
  }

  function handleFeedback() {
    trackFeedbackOpen();
    setFeedbackOpen(true);
  }

  function handleMinimize() {
    setMinimized(true);
    try {
      localStorage.setItem(SITE_BETA_TOOLBAR_MINIMIZED_KEY, '1');
    } catch {
      /* ignore */
    }
  }

  function handleRestore() {
    setMinimized(false);
    try {
      localStorage.setItem(SITE_BETA_TOOLBAR_MINIMIZED_KEY, '0');
    } catch {
      /* ignore */
    }
  }

  return (
    <>
      {minimized ? (
        <button
          type="button"
          className="beta-toolbar__restore"
          onClick={handleRestore}
          aria-label="Show beta toolbar"
        >
          <ChevronUpIcon />
        </button>
      ) : (
        <aside
          className="beta-toolbar"
          aria-label="Help us improve shops.mt during beta"
        >
          <div className="beta-toolbar__inner">
            <div className="beta-toolbar__message">
              <span className="beta-toolbar__badge">BETA</span>
              <p className="beta-toolbar__copy">{SITE_BETA_TOOLBAR_COPY}</p>
            </div>
            <div className="beta-toolbar__actions">
              <button
                type="button"
                className="beta-toolbar__action"
                onClick={handleShare}
                aria-label="Share site"
              >
                <ShareIcon />
                <span>Share site</span>
              </button>
              <FacebookFollowLink
                location="beta_toolbar"
                variant="toolbar"
                className="beta-toolbar__action"
              >
                Follow on FB
              </FacebookFollowLink>
              <button
                type="button"
                className="beta-toolbar__action"
                onClick={handleFeedback}
                aria-label="Give feedback"
                aria-haspopup="dialog"
                aria-expanded={feedbackOpen}
              >
                <FeedbackIcon />
                <span>Feedback</span>
              </button>
            </div>
            <button
              type="button"
              className="beta-toolbar__minimize"
              onClick={handleMinimize}
              aria-label="Minimize beta toolbar"
            >
              <ChevronDownIcon />
            </button>
          </div>
        </aside>
      )}
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </>
  );
}
